import React from 'react'
import { Form, Button } from 'react-bootstrap'
import { FamilyMember, RelationType, PlanDefinition, PremiumType } from '../types'

interface Props {
  members: FamilyMember[]
  selectedPlan: PlanDefinition | null
  premiumType: PremiumType | null
  memberPremiums?: Record<number, number>
  availableRelationsToAdd: RelationType[]
  maxLivesReached: boolean
  isParentBlocked: boolean
  isParentInLawBlocked: boolean
  onAddMember: (relation: RelationType) => void
  onUpdateMember: (id: number, updates: Partial<FamilyMember>) => void
  onRemoveMember: (id: number) => void
  planSelector?: React.ReactNode
  planName?: string | null
}

const RELATION_LABELS: Record<string, string> = {
  Self: 'Self',
  Spouse: 'Spouse',
  Child: 'Child',
  Parent: 'Parent',
  ParentInLaw: 'Parent-in-Law',
  Sibling: 'Sibling',
  Brother: 'Brother',
  Sister: 'Sister',
}

const RELATION_ICONS: Record<string, string> = {
  Self: 'bi-person-fill',
  Spouse: 'bi-heart-fill',
  Child: 'bi-emoji-smile-fill',
  Parent: 'bi-people-fill',
  ParentInLaw: 'bi-people-fill',
  Sibling: 'bi-person-hearts',
  Brother: 'bi-person-hearts',
  Sister: 'bi-person-hearts',
}

const RELATION_COLORS: Record<string, string> = {
  Self: 'primary',
  Spouse: 'primary',
  Child: 'primary',
  Parent: 'primary',
  ParentInLaw: 'primary',
  Sibling: 'primary',
  Brother: 'primary',
  Sister: 'primary',
}

const formatCurrency = (amount: number): string => '₹' + amount.toLocaleString('en-IN')

// Scoped CSS for animations
const styles = `
@keyframes memberSlideIn {
  from { opacity: 0; transform: translateY(-12px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes memberPulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(0,158,247,0); }
  50% { box-shadow: 0 0 0 4px rgba(0,158,247,0.1); }
}
.member-card {
  animation: memberSlideIn 0.3s ease-out;
  transition: all 0.2s ease;
}
.member-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
}
.member-card-self {
}
.member-add-btn {
  transition: all 0.2s ease;
}
.member-add-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.12);
}
.member-remove-btn {
  opacity: 1;
  transition: all 0.15s ease;
}
.member-remove-btn:hover {
  opacity: 1;
  transform: scale(1.15);
}
.member-counter {
  background: #084298;
  color: white;
  border-radius: 20px;
  padding: 2px 12px;
  font-size: 0.8rem;
  font-weight: 600;
}
.member-icon {
  color: #084298;
}
.member-badge {
  background-color: #e8eef6;
  color: #084298;
  font-weight: 600;
}
.member-section-title {
  color: #084298;
}
.member-add-btn-unified {
  color: #084298;
  border-color: #084298;
  transition: all 0.2s ease;
}
.member-add-btn-unified:hover {
  background: #084298;
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.12);
}
/* Desktop: table header visible */
.member-table-header { display: flex; }
/* Desktop: row layout horizontal */
.member-row-desktop { display: flex; }
.member-row-mobile { display: none; }

@media (max-width: 768px) {
  .member-table-header { display: none !important; }
  .member-row-desktop { display: none !important; }
  .member-row-mobile { display: block !important; }
}
`

const FamilyMemberForm: React.FC<Props> = ({
  members,
  selectedPlan,
  premiumType,
  memberPremiums = {},
  availableRelationsToAdd,
  maxLivesReached,
  isParentBlocked,
  isParentInLawBlocked,
  onAddMember,
  onUpdateMember,
  onRemoveMember,
  planSelector,
  planName,
}) => {
  return (
    <div className='mb-6'>
      <style>{styles}</style>

      {/* Section Header */}
      <div className='d-flex justify-content-between align-items-center mb-4'>
        <div className='d-flex align-items-center gap-3'>
          <div>
            <h4 className='fs-6 fw-bolder mb-0'>
              <i className='bi bi-people-fill member-section-title me-2'></i>
              Family Members
            </h4>
            {planName && (
              <span className='member-badge badge mt-1 fs-7'>{planName}</span>
            )}
          </div>
        </div>
        {selectedPlan && (
          <span className='member-counter'>
            {members.length} / {selectedPlan.maxLives}
          </span>
        )}
      </div>

      {/* Compact plan selector (if multiple plans) */}
      {planSelector && <div className='mb-4'>{planSelector}</div>}

      {/* Member List */}
      <div className='border rounded-3 overflow-hidden'>
        {/* Table Header — desktop only */}
        <div className='member-table-header align-items-center gap-2 px-3 py-2 bg-white'>
          <div className='flex-shrink-0' style={{ width: '32px' }}></div>
          <span className='flex-shrink-0 fw-bold fs-7 text-dark' style={{ width: '75px', textAlign: 'center' }}>Relation</span>
          <span className='flex-grow-1 fw-bold fs-7 text-dark'>Name</span>
          <span className='fw-bold fs-7 text-dark flex-shrink-0' style={{ width: '140px' }}>Date of Birth</span>
          <span className='fw-bold fs-7 text-dark flex-shrink-0' style={{ width: '110px' }}>Gender</span>
          {premiumType === 'Individual' && <span className='flex-shrink-0 fw-bold fs-7 text-dark' style={{ width: '90px', textAlign: 'center' }}>Premium</span>}
          <span className='flex-shrink-0' style={{ width: '30px' }}></span>
        </div>
        {members.map((member, index) => {
          const isSelf = member.relation === 'Self'
          const isParent = member.relation === 'Parent' || member.relation === 'ParentInLaw'
          const color = RELATION_COLORS[member.relation] || 'primary'
          const icon = RELATION_ICONS[member.relation] || 'bi-person-fill'
          return (
            <div
              key={member.id}
              className={`member-card bg-white px-3 py-2 ${
                isSelf ? 'member-card-self' : ''
              }`}
              style={{ animationDelay: `${index * 0.05}s`, borderTop: '1px solid #eee' }}
            >
              {/* Desktop row */}
              <div className='member-row-desktop align-items-center gap-2'>
                <div
                  className='d-flex align-items-center justify-content-center rounded-circle flex-shrink-0'
                  style={{ width: '32px', height: '32px', backgroundColor: '#e8eef6' }}
                >
                  <i className={`bi ${icon} member-icon fs-6`}></i>
                </div>
                <span
                  className='member-badge badge fs-7 flex-shrink-0'
                  style={{ width: '75px', textAlign: 'center' }}
                >
                  {RELATION_LABELS[member.relation] || member.relation}
                </span>
                <Form.Control
                  type='text'
                  size='sm'
                  value={member.name}
                  disabled={isSelf}
                  onChange={(e) => onUpdateMember(member.id, { name: e.target.value })}
                  placeholder='Full name'
                  className={`flex-grow-1 rounded ${isSelf ? 'border-0 bg-transparent' : 'border border-gray-300'}`}
                />
                <Form.Control
                  type='date'
                  size='sm'
                  value={member.dob}
                  disabled={isSelf}
                  onChange={(e) => onUpdateMember(member.id, { dob: e.target.value })}
                  className={`flex-shrink-0 rounded ${isSelf ? 'border-0 bg-transparent' : 'border border-gray-300'}`}
                  style={{ width: '140px' }}
                />
                <Form.Select
                  size='sm'
                  value={member.gender}
                  disabled={isSelf}
                  onChange={(e) => onUpdateMember(member.id, { gender: e.target.value })}
                  className={`flex-shrink-0 rounded ${isSelf ? 'border-0 bg-transparent' : 'border border-gray-300'}`}
                  style={{ width: '110px' }}
                >
                  <option value=''>Gender</option>
                  <option value='Male'>Male</option>
                  <option value='Female'>Female</option>
                  {!isParent && <option value='Other'>Other</option>}
                </Form.Select>
                {premiumType === 'Individual' && (
                  <span className='badge fs-7 flex-shrink-0 fw-bolder text-center' style={{ width: '90px' }}>
                    {(memberPremiums[member.id] ?? member.premiumAmount) > 0 ? (
                      <span className='badge badge-light-success fs-7 fw-bolder'>{formatCurrency(memberPremiums[member.id] ?? member.premiumAmount)}</span>
                    ) : (
                      <span className='text-muted'>—</span>
                    )}
                  </span>
                )}
                {!isSelf && (
                  <button
                    className='btn btn-icon btn-sm member-remove-btn flex-shrink-0'
                    onClick={() => onRemoveMember(member.id)}
                    title='Remove member'
                    style={{ width: '30px' }}
                  >
                    <i className='bi bi-x-circle text-danger fs-6'></i>
                  </button>
                )}
                {isSelf && <div className='flex-shrink-0' style={{ width: '30px' }}></div>}
              </div>

              {/* Mobile card layout */}
              <div className='member-row-mobile'>
                <div className='d-flex justify-content-between align-items-center mb-2'>
                  <div className='d-flex align-items-center gap-2'>
                    <div
                      className='d-flex align-items-center justify-content-center rounded-circle flex-shrink-0'
                      style={{ width: '28px', height: '28px', backgroundColor: '#e8eef6' }}
                    >
                      <i className={`bi ${icon} member-icon fs-7`}></i>
                    </div>
                    <span className='member-badge badge fs-7'>
                      {RELATION_LABELS[member.relation] || member.relation}
                    </span>
                    {premiumType === 'Individual' && (memberPremiums[member.id] ?? member.premiumAmount) > 0 && (
                      <span className='badge badge-light-success fs-7 fw-bolder'>
                        {formatCurrency(memberPremiums[member.id] ?? member.premiumAmount)}
                      </span>
                    )}
                  </div>
                  {!isSelf && (
                    <button
                      className='btn btn-icon btn-sm member-remove-btn'
                      onClick={() => onRemoveMember(member.id)}
                      title='Remove member'
                    >
                      <i className='bi bi-x-circle text-danger fs-7'></i>
                    </button>
                  )}
                </div>
                <div className='d-flex flex-column gap-2'>
                  <Form.Control
                    type='text'
                    size='sm'
                    value={member.name}
                    disabled={isSelf}
                    onChange={(e) => onUpdateMember(member.id, { name: e.target.value })}
                    placeholder='Full name'
                    className={`rounded ${isSelf ? 'border-0 bg-transparent' : 'border border-gray-300'}`}
                  />
                  <div className='d-flex gap-2'>
                    <Form.Control
                      type='date'
                      size='sm'
                      value={member.dob}
                      disabled={isSelf}
                      onChange={(e) => onUpdateMember(member.id, { dob: e.target.value })}
                      className={`rounded flex-grow-1 ${isSelf ? 'border-0 bg-transparent' : 'border border-gray-300'}`}
                    />
                    <Form.Select
                      size='sm'
                      value={member.gender}
                      disabled={isSelf}
                      onChange={(e) => onUpdateMember(member.id, { gender: e.target.value })}
                      className={`rounded ${isSelf ? 'border-0 bg-transparent' : 'border border-gray-300'}`}
                      style={{ maxWidth: '120px' }}
                    >
                      <option value=''>Gender</option>
                      <option value='Male'>Male</option>
                      <option value='Female'>Female</option>
                      {!isParent && <option value='Other'>Other</option>}
                    </Form.Select>
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Add Member Buttons */}
      {!maxLivesReached && availableRelationsToAdd.length > 0 && (
        <div className='d-flex flex-wrap gap-2 mt-4'>
          {availableRelationsToAdd.map((relation) => {
            const color = RELATION_COLORS[relation] || 'primary'
            const icon = RELATION_ICONS[relation] || 'bi-person-plus-fill'
            return (
              <button
                key={relation}
                className='btn btn-sm member-add-btn-unified d-flex align-items-center gap-1 px-3'
                onClick={() => onAddMember(relation)}
              >
                <i className={`bi ${icon} fs-8`}></i>
                <span>{RELATION_LABELS[relation] || relation}</span>
              </button>
            )
          })}
        </div>
      )}

      {/* Hints */}
      {isParentBlocked && (
        <div className='alert alert-light-warning border-0 fs-8 py-2 px-3 mt-3 mb-0 d-flex align-items-center'>
          <i className='bi bi-info-circle-fill text-warning me-2'></i>
          Parent-in-Law already added — cannot add Parent (either/or policy)
        </div>
      )}
      {isParentInLawBlocked && (
        <div className='alert alert-light-warning border-0 fs-8 py-2 px-3 mt-3 mb-0 d-flex align-items-center'>
          <i className='bi bi-info-circle-fill text-warning me-2'></i>
          Parent already added — cannot add Parent-in-Law (either/or policy)
        </div>
      )}
      {maxLivesReached && (
        <div className='alert alert-light-danger border-0 fs-8 py-2 px-3 mt-3 mb-0 d-flex align-items-center'>
          <i className='bi bi-exclamation-triangle-fill text-danger me-2'></i>
          Maximum {selectedPlan?.maxLives} members reached
        </div>
      )}
    </div>
  )
}

export default FamilyMemberForm
